import React from 'react';
import {Map} from 'immutable';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import withAttribute from './withAttribute';
import {buttonStyle} from './styles';
import {
  DATA_ATTR_NODE_TYPE,
  DATA_ATTR_PUBLISHABLE_KEY,
  NODE_TYPE_COMMERCE_BUY_NOW_BUTTON,
  DATA_ATTR_DEFAULT_TEXT,
  DATA_ATTR_SUBSCRIPTION_TEXT,
} from '@packages/systems/commerce/constants';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {withMeta, Plugins, Atom} from '@packages/systems/wfdl';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {
  getArrayOrElse,
  getInPojo,
  getNumberOrElse,
  getString,
} from '@packages/utilities/data-utils';

const DEFAULT_TEXT_KEY = 'DEFAULT';
const SUBSCRIPTION_TEXT_KEY = 'SUBSCRIPTION_BUTTON_DEFAULT';

const textStates = {
  [DEFAULT_TEXT_KEY]: {
    id: DEFAULT_TEXT_KEY,
    name: 'Default text',
    copy: 'Buy now',
  },
  [SUBSCRIPTION_TEXT_KEY]: {
    id: SUBSCRIPTION_TEXT_KEY,
    name: 'Subscription text',
    copy: 'Subscribe now',
  },
} as const;

const CommerceBuyNowButton = Atom.createRender((props) => {
  const {
    attributes,
    classes,
    context,
    data,
    environmentContext,
    generateAttributes,
  } = props;

  const texts = getArrayOrElse(getInPojo(data, ['texts']), []);
  const index = getNumberOrElse(getInPojo(data, ['temp', 'index']), 0);
  const defaultText = getString(getInPojo(texts, [0, 'copy']));
  const subscriptionText = getString(
    getInPojo(texts, [texts.length - 1, 'copy'])
  );
  const value = getString(getInPojo(texts, [index, 'copy']));
  const ariaBusy = index !== 0;

  let newAttributes = attributes.merge(
    Map({'aria-busy': ariaBusy ? 'true' : 'false'}),
    Map({'aria-haspopup': 'false'})
  );
  if (typeof defaultText === 'string') {
    newAttributes = newAttributes.merge(
      Map({[DATA_ATTR_DEFAULT_TEXT]: defaultText})
    );
  }
  if (typeof subscriptionText === 'string') {
    newAttributes = newAttributes.merge(
      Map({[DATA_ATTR_SUBSCRIPTION_TEXT]: subscriptionText})
    );
  }

  const publishableKey = context.get('ecommPublishableKey');
  const styledAttributes = context.get('showBuyNowButton')
    ? newAttributes
    : newAttributes.merge(Map({style: Map({display: 'none'})}));

  const customNodeState = environmentContext.get('customNodeState');
  let updatedClasses = classes;
  if (
    customNodeState &&
    customNodeState.includes('ecommerce-buy-now-disabled')
  ) {
    updatedClasses = updatedClasses.push(`w--ecommerce-buy-now-disabled`);
  }

  return (
    <a
      {...generateAttributes(styledAttributes, updatedClasses)}
      href="/checkout"
      {...{
        [DATA_ATTR_PUBLISHABLE_KEY]: publishableKey,
      }}
    >
      {value}
    </a>
  );
});

const config = Atom.create({
  displayName: 'Buy Now Button',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Button')),
  },
  showEmpty: false,
  constraints: {
    ancestors: [
      {
        is: 'CommerceAddToCartForm',
        rule: 'ExactlyOne',
      },
    ],
  },
  style: {
    base: {
      // This FlowFixMe is here because ecommerce-buy-now-disabled isn't technically a pseudo, but a custom node state

      main: {
        noPseudo: {
          ...buttonStyle,
          display: 'inline-block',
          marginTop: px(10),
        },
        // @ts-expect-error - TS2322 - Type '{ noPseudo: { display: "inline-block"; marginTop: string; alignContent?: AlignContent | undefined; alignItems?: AlignItems | undefined; alignSelf?: BoxItemAlignment | undefined; ... 103 more ...; visibility?: Visibility | undefined; }; 'ecommerce-buy-now-disabled': { ...; }; }' is not assignable to type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>>'.
        'ecommerce-buy-now-disabled': {
          color: createRgbaColor('#666666'),
          backgroundColor: createRgbaColor('#e6e6e6'),
          borderTopColor: createRgbaColor('#e6e6e6'),
          borderBottomColor: createRgbaColor('#e6e6e6'),
          borderLeftColor: createRgbaColor('#e6e6e6'),
          borderRightColor: createRgbaColor('#e6e6e6'),
          cursor: 'not-allowed',
          outlineStyle: 'none',
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_BUY_NOW_BUTTON
  )(CommerceBuyNowButton),
  dataType: withMeta(
    {
      textStates,
    },
    createIntegratedRootAliasInstance(
      qualify(Plugins.name('Commerce'), typeName('MultiTextEditAlias'))
    )
  ),
});

export default config;
